import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import api from "../../api/api";

const KEY_STAGE_ORDER = [
  "EYFS",
  "KS1",
  "KS2",
  "KS3",
  "KS4",
  "KS5",
];

function getYearOrder(name) {
  if (!name) {
    return 999;
  }

  const normalised = name.trim().toLowerCase();

  if (normalised === "nursery") {
    return -1;
  }

  if (normalised === "reception") {
    return 0;
  }

  const match = normalised.match(/^(?:year\s+|y)(\d+)$/);

  if (match) {
    return Number(match[1]);
  }

  return 999;
}

function formatGender(code) {
  if (!code) {
    return "Not recorded";
  }

  if (code === "MALE") {
    return "Male";
  }

  if (code === "FEMALE") {
    return "Female";
  }

  return code
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function formatDateTime(value) {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  return date.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function ArborDashboard() {
  const [schools, setSchools] = useState([]);
  const [trust, setTrust] = useState(null);
  const [analytics, setAnalytics] = useState([]);

  const [selectedSchool, setSelectedSchool] =
    useState("");

  const [selectedKeyStage, setSelectedKeyStage] =
    useState("");

  const [selectedGender, setSelectedGender] =
    useState("");

  const [selectedYear, setSelectedYear] =
    useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] =
    useState("");

  async function loadDashboard(showLoading = false) {
    try {
      if (showLoading) {
        setLoading(true);
      }

      setError("");

      const [dashboardData, analyticsData] =
        await Promise.all([
          api("/arbor/dashboard"),
          api("/arbor/students/analytics"),
        ]);

      setSchools(
        Array.isArray(dashboardData?.schools)
          ? dashboardData.schools
          : []
      );

      setTrust(dashboardData?.trust || null);

      setAnalytics(
        Array.isArray(analyticsData?.data)
          ? analyticsData.data
          : []
      );
    } catch (err) {
      setError(
        err.message ||
          "Failed to load Arbor dashboard"
      );
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    loadDashboard(true);
  }, []);

  const selectedSchoolData = useMemo(() => {
    if (!selectedSchool) {
      return null;
    }

    return (
      schools.find(
        (school) =>
          Number(school.schoolId) ===
          Number(selectedSchool)
      ) || null
    );
  }, [schools, selectedSchool]);

  const availableKeyStages = useMemo(() => {
    const values = new Set(
      analytics
        .map((row) => row.keyStage)
        .filter(Boolean)
    );

    return KEY_STAGE_ORDER.filter((keyStage) =>
      values.has(keyStage)
    );
  }, [analytics]);

  const availableGenders = useMemo(() => {
    return [
      ...new Set(
        analytics
          .map((row) => row.genderCode)
          .filter(Boolean)
      ),
    ].sort();
  }, [analytics]);

  const filteredAnalytics = useMemo(() => {
    return analytics.filter((row) => {
      if (
        selectedSchool &&
        Number(row.schoolId) !==
          Number(selectedSchool)
      ) {
        return false;
      }

      if (
        selectedKeyStage &&
        row.keyStage !== selectedKeyStage
      ) {
        return false;
      }

      if (
        selectedGender &&
        row.genderCode !== selectedGender
      ) {
        return false;
      }

      return true;
    });
  }, [
    analytics,
    selectedSchool,
    selectedKeyStage,
    selectedGender,
  ]);

  const filteredStudentCount = useMemo(() => {
    return filteredAnalytics.reduce(
      (total, row) =>
        total + Number(row.studentCount || 0),
      0
    );
  }, [filteredAnalytics]);

  const studentPhaseTotals = useMemo(() => {
    const totals = {
      nursery: 0,
      primary: 0,
      secondary: 0,
      sixthForm: 0,
    };

    filteredAnalytics.forEach((row) => {
      const name = row.academicLevelName;
      const count = Number(row.studentCount || 0);
      const yearOrder = getYearOrder(name);

      if (name?.trim().toLowerCase() === "nursery") {
        totals.nursery += count;
        return;
      }

      if (yearOrder >= 0 && yearOrder <= 6) {
        totals.primary += count;
        return;
      }

      if (yearOrder >= 7 && yearOrder <= 11) {
        totals.secondary += count;
        return;
      }

      if (yearOrder >= 12 && yearOrder <= 13) {
        totals.sixthForm += count;
      }
    });

    return totals;
  }, [filteredAnalytics]);

  const studentsBySchool = useMemo(() => {
    const totals = new Map();

    filteredAnalytics.forEach((row) => {
      const existing = totals.get(row.schoolId) || {
        schoolId: row.schoolId,
        schoolName: row.schoolName,
        schoolShortCode:
          row.schoolShortCode || row.schoolName,
        studentCount: 0,
      };

      existing.studentCount += Number(
        row.studentCount || 0
      );

      totals.set(row.schoolId, existing);
    });

    return [...totals.values()].sort((a, b) =>
      a.schoolName.localeCompare(b.schoolName)
    );
  }, [filteredAnalytics]);

  const studentsByKeyStage = useMemo(() => {
    const totals = new Map();

    filteredAnalytics.forEach((row) => {
      const keyStage = row.keyStage || "Unknown";

      totals.set(
        keyStage,
        (totals.get(keyStage) || 0) +
          Number(row.studentCount || 0)
      );
    });

    return [...totals.entries()]
      .map(([keyStage, studentCount]) => ({
        keyStage,
        studentCount,
      }))
      .sort((a, b) => {
        const aIndex =
          KEY_STAGE_ORDER.indexOf(a.keyStage);

        const bIndex =
          KEY_STAGE_ORDER.indexOf(b.keyStage);

        if (aIndex === -1 && bIndex === -1) {
          return a.keyStage.localeCompare(
            b.keyStage
          );
        }

        if (aIndex === -1) {
          return 1;
        }

        if (bIndex === -1) {
          return -1;
        }

        return aIndex - bIndex;
      });
  }, [filteredAnalytics]);

  const studentsByYear = useMemo(() => {
    const totals = new Map();

    filteredAnalytics.forEach((row) => {
      const name =
        row.academicLevelName || "Unknown";

      totals.set(
        name,
        (totals.get(name) || 0) +
          Number(row.studentCount || 0)
      );
    });

    return [...totals.entries()]
      .map(
        ([academicLevelName, studentCount]) => ({
          academicLevelName,
          studentCount,
        })
      )
      .sort(
        (a, b) =>
          getYearOrder(a.academicLevelName) -
          getYearOrder(b.academicLevelName)
      );
  }, [filteredAnalytics]);

  const studentsByRegistrationForm = useMemo(() => {
    if (!selectedYear) {
      return [];
    }

    const totals = new Map();

    filteredAnalytics
      .filter(
        (row) =>
          row.academicLevelName === selectedYear
      )
      .forEach((row) => {
        const formName =
          row.registrationFormName ||
          "Not recorded";

        const schoolCode =
          row.schoolShortCode ||
          row.schoolName;

        const key =
          `${row.schoolId}-${formName}`;

        const existing = totals.get(key) || {
          schoolId: row.schoolId,
          schoolName: row.schoolName,
          schoolShortCode: schoolCode,
          registrationFormName: formName,
          chartLabel:
            `${schoolCode} – ${formName}`,
          studentCount: 0,
        };

        existing.studentCount += Number(
          row.studentCount || 0
        );

        totals.set(key, existing);
      });

    return [...totals.values()].sort((a, b) => {
      const schoolComparison =
        a.schoolShortCode.localeCompare(
          b.schoolShortCode,
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          }
        );

      if (schoolComparison !== 0) {
        return schoolComparison;
      }

      return a.registrationFormName.localeCompare(
        b.registrationFormName,
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );
    });
  }, [filteredAnalytics, selectedYear]);

  const studentsByGender = useMemo(() => {
    const totals = new Map();

    filteredAnalytics.forEach((row) => {
      const code =
        row.genderCode || "UNKNOWN";

      totals.set(
        code,
        (totals.get(code) || 0) +
          Number(row.studentCount || 0)
      );
    });

    return [...totals.entries()]
      .map(([genderCode, studentCount]) => ({
        genderCode,
        genderName: formatGender(genderCode),
        studentCount,
      }))
      .sort((a, b) =>
        a.genderName.localeCompare(b.genderName)
      );
  }, [filteredAnalytics]);

  const clearFilters = () => {
    setSelectedSchool("");
    setSelectedKeyStage("");
    setSelectedGender("");
    setSelectedYear("");
    setRefreshMessage("");
  };

  const handleRefresh = async () => {
    if (
      !selectedSchoolData ||
      !selectedSchoolData.arborEnabled ||
      refreshing
    ) {
      return;
    }

    try {
      setRefreshing(true);
      setRefreshMessage("");
      setError("");

      const result = await api(
        `/arbor/sync/${selectedSchoolData.schoolId}`,
        {
          method: "POST",
        }
      );

      if (result?.success === false) {
        throw new Error(
          result.error || "Arbor refresh failed"
        );
      }

      await loadDashboard(false);

      setRefreshMessage(
        `${selectedSchoolData.schoolName} refreshed successfully.`
      );
    } catch (err) {
      setRefreshMessage(
        err.message || "Failed to refresh Arbor data"
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-2xl font-bold">
          Arbor Dashboard
        </h1>

        <p className="text-gray-600">
          Loading Arbor analytics...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-2xl font-bold">
          Arbor Dashboard
        </h1>

        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Heading */}

      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Arbor Analytics
          </h1>

          <p className="mt-1 text-gray-600">
            Trust-wide staff and student analytics.
          </p>

          {selectedSchoolData?.syncStatus
            ?.lastSuccessfulAt && (
            <p className="mt-2 text-sm text-gray-500">
              Last updated:{" "}
              {formatDateTime(
                selectedSchoolData.syncStatus
                  .lastSuccessfulAt
              )}
            </p>
          )}
        </div>

        {selectedSchoolData?.arborEnabled && (
          <div className="flex flex-col items-start gap-2 md:items-end">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {refreshing
                ? "Refreshing..."
                : "Refresh now"}
            </button>

            {refreshMessage && (
              <div className="text-sm text-gray-600">
                {refreshMessage}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Trust summary */}

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <div className="text-sm font-medium text-gray-500">
            Students
          </div>

          <div className="mt-2 text-3xl font-bold text-gray-900">
            {filteredStudentCount.toLocaleString()}
          </div>

          {(selectedSchool ||
            selectedKeyStage ||
            selectedGender) && (
            <div className="mt-1 text-xs text-gray-500">
              Filtered from{" "}
              {Number(
                trust?.studentCount || 0
              ).toLocaleString()}
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <div className="text-sm font-medium text-gray-500">
            Unique Trust Staff
          </div>

          <div className="mt-2 text-3xl font-bold text-gray-900">
            {Number(
              trust?.uniqueStaffCount || 0
            ).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Student phase summary */}

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-lg border bg-white px-4 py-3 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Nursery
          </div>

          <div className="mt-1 text-xl font-bold text-gray-900">
            {studentPhaseTotals.nursery.toLocaleString()}
          </div>
        </div>

        <div className="rounded-lg border bg-white px-4 py-3 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Primary
          </div>

          <div className="mt-1 text-xl font-bold text-gray-900">
            {studentPhaseTotals.primary.toLocaleString()}
          </div>
        </div>

        <div className="rounded-lg border bg-white px-4 py-3 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Secondary
          </div>

          <div className="mt-1 text-xl font-bold text-gray-900">
            {studentPhaseTotals.secondary.toLocaleString()}
          </div>
        </div>

        <div className="rounded-lg border bg-white px-4 py-3 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Sixth Form
          </div>

          <div className="mt-1 text-xl font-bold text-gray-900">
            {studentPhaseTotals.sixthForm.toLocaleString()}
          </div>
        </div>
      </div>

      {/* School summary */}

      <div className="mb-6 overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            School Summary
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  School
                </th>

                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                  Staff
                </th>

                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                  Students
                </th>

                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                  Last updated
                </th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {schools.map((school) => (
                <tr key={school.schoolId}>
                  <td className="px-6 py-4 font-medium">
                    {school.schoolName}
                  </td>

                  <td className="px-6 py-4 text-right">
                    {Number(
                      school.staffCount || 0
                    ).toLocaleString()}
                  </td>

                  <td className="px-6 py-4 text-right">
                    {Number(
                      school.studentCount || 0
                    ).toLocaleString()}
                  </td>

                  <td className="px-6 py-4 text-right text-sm text-gray-500">
                    {school.arborEnabled
                      ? formatDateTime(
                          school.syncStatus
                            ?.lastSuccessfulAt
                        )
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters */}

      <div className="mb-6 rounded-lg border bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Filters
          </h2>

          {(selectedSchool ||
            selectedKeyStage ||
            selectedGender ||
            selectedYear) && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label
              htmlFor="school-filter"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              School
            </label>

            <select
              id="school-filter"
              value={selectedSchool}
              onChange={(event) => {
                setSelectedSchool(
                  event.target.value
                );
                setSelectedYear("");
                setRefreshMessage("");
              }}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2"
            >
              <option value="">
                All schools
              </option>

              {schools.map((school) => (
                <option
                  key={school.schoolId}
                  value={school.schoolId}
                >
                  {school.schoolName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="key-stage-filter"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Key Stage
            </label>

            <select
              id="key-stage-filter"
              value={selectedKeyStage}
              onChange={(event) => {
                setSelectedKeyStage(
                  event.target.value
                );
                setSelectedYear("");
              }}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2"
            >
              <option value="">
                All key stages
              </option>

              {availableKeyStages.map(
                (keyStage) => (
                  <option
                    key={keyStage}
                    value={keyStage}
                  >
                    {keyStage}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label
              htmlFor="gender-filter"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Gender
            </label>

            <select
              id="gender-filter"
              value={selectedGender}
              onChange={(event) =>
                setSelectedGender(
                  event.target.value
                )
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2"
            >
              <option value="">
                All genders
              </option>

              {availableGenders.map((gender) => (
                <option
                  key={gender}
                  value={gender}
                >
                  {formatGender(gender)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* School / Key Stage */}

      <div className="mb-6 grid gap-6 xl:grid-cols-2">
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-lg font-semibold">
            Students by School
          </h2>

          <div className="h-[350px]">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart data={studentsBySchool}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="schoolShortCode"
                  interval={0}
                />

                <YAxis allowDecimals={false} />

                <Tooltip
                  formatter={(value) => [
                    Number(value).toLocaleString(),
                    "Students",
                  ]}
                  labelFormatter={(
                    shortCode,
                    payload
                  ) =>
                    payload?.[0]?.payload
                      ?.schoolName || shortCode
                  }
                />

                <Bar
                  dataKey="studentCount"
                  name="Students"
                  fill="#16a34a"
                  cursor="pointer"
                  onClick={(data) => {
                    const schoolId = data?.schoolId;

                    if (!schoolId) {
                      return;
                    }

                    setSelectedSchool((current) =>
                      Number(current) === Number(schoolId)
                        ? ""
                        : String(schoolId)
                    );

                    setSelectedYear("");
                    setRefreshMessage("");
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-lg font-semibold">
            Students by Key Stage
          </h2>

          <div className="h-[350px]">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart data={studentsByKeyStage}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="keyStage" />

                <YAxis allowDecimals={false} />

                <Tooltip
                  formatter={(value) =>
                    Number(value).toLocaleString()
                  }
                />

                <Bar
                  dataKey="studentCount"
                  name="Students"
                  fill="#2563eb"
                  cursor="pointer"
                  onClick={(data) => {
                    const keyStage = data?.keyStage;

                    if (!keyStage) {
                      return;
                    }

                    setSelectedKeyStage((current) =>
                      current === keyStage
                        ? ""
                        : keyStage
                    );

                    setSelectedYear("");
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Year / Gender */}

      <div className="mb-6 grid gap-6 xl:grid-cols-2">
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-lg font-semibold">
            Students by Year Group
          </h2>

          <div className="h-[350px]">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart data={studentsByYear}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="academicLevelName"
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={70}
                />

                <YAxis allowDecimals={false} />

                <Tooltip
                  formatter={(value) =>
                    Number(value).toLocaleString()
                  }
                />

                <Bar
                  dataKey="studentCount"
                  name="Students"
                  fill="#7c3aed"
                  cursor="pointer"
                  onClick={(data) => {
                    const year =
                      data?.academicLevelName;

                    if (!year) {
                      return;
                    }

                    setSelectedYear((current) =>
                      current === year ? "" : year
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-lg font-semibold">
            Students by Gender
          </h2>

          <div className="h-[350px]">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart data={studentsByGender}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="genderName" />

                <YAxis allowDecimals={false} />

                <Tooltip
                  formatter={(value) =>
                    Number(value).toLocaleString()
                  }
                />

                <Bar
                  dataKey="studentCount"
                  name="Students"
                  fill="#db2777"
                  cursor="pointer"
                  onClick={(data) => {
                    const gender = data?.genderCode;

                    if (!gender) {
                      return;
                    }

                    setSelectedGender((current) =>
                      current === gender ? "" : gender
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Registration form drill-down */}

      {selectedYear && (
        <div className="mb-6 rounded-lg border bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">
                {selectedYear} — Registration Forms
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                {selectedSchool
                  ? "Showing the selected school"
                  : "Showing all schools"}

                {selectedGender
                  ? ` • ${formatGender(
                      selectedGender
                    )}`
                  : ""}
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                setSelectedYear("")
              }
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              Clear year
            </button>
          </div>

          {studentsByRegistrationForm.length ===
          0 ? (
            <div className="py-8 text-center text-gray-500">
              No registration form data available.
            </div>
          ) : (
            <div className="h-[350px]">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={
                    studentsByRegistrationForm
                  }
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="chartLabel"
                    interval={0}
                  />

                  <YAxis allowDecimals={false} />

                  <Tooltip
                    formatter={(value) => [
                      Number(
                        value
                      ).toLocaleString(),
                      "Students",
                    ]}
                    labelFormatter={(
                      label,
                      payload
                    ) => {
                      const row =
                        payload?.[0]?.payload;

                      if (!row) {
                        return label;
                      }

                      return `${row.schoolName} (${row.schoolShortCode}) — ${row.registrationFormName}`;
                    }}
                  />

                  <Bar
                    dataKey="studentCount"
                    name="Students"
                    fill="#0891b2"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}